import {
  Button,
  SelectMenu,
  TextField,
  Spinner,
  Progress,
  Typography,
  Accordion,
} from '@scale/scaleui';
import { useState, useEffect } from 'react';
import {
  GenerateUnitTestsRequest,
  RunUnitTestsRequest,
  RunUnitTestsResponse,
} from '@scale/shared/types/chat';
import API from '@scale/frontend-lib/dashboard-api';
import { useMutation } from 'react-query';
import { TextCollectionField, UnitField } from '@scale/shared/types/params/textcollection';

enum Language {
  Python = 'Python',
  Java = 'Java',
  JavaScript = 'JavaScript',
  CPlusPlus = 'C++',
}

export enum Format {
  JSON = 'JSON',
  PyTest = 'PyTest',
  Jest = 'Jest',
  StdIn = 'StdIn',
}

const formatConfig = {
  [Language.Python]: {
    default: Format.PyTest,
    allowed: [Format.PyTest, Format.JSON],
  },
  [Language.Java]: {
    default: Format.StdIn,
    allowed: [Format.StdIn],
  },
  [Language.JavaScript]: {
    default: Format.Jest,
    allowed: [Format.Jest],
  },
  [Language.CPlusPlus]: {
    default: Format.StdIn,
    allowed: [Format.StdIn],
  },
};

export type CodingUnitTestsGenerationConfig = {
  enableCodingUnitTestsGeneration?: boolean;
  codeFieldId?: string;
  useCodeFromMostRecentResponseEditor?: boolean;
  unitTestsFieldId?: string;
  numberOfTestCases?: number;
  enableRunTests?: boolean;
  enableRunHiddenTests?: boolean;
};

interface UnitTestGenerationFormProps {
  codingUnitTestsGenerationConfig?: CodingUnitTestsGenerationConfig;
  annotations: Record<string, any>;
  setAnnotations: (newAnnotations: any) => void;
  projectId: string | undefined;
  assignmentId: string | undefined;
  lastPrompt?: string | null;
  lastModelResponseEditorContent?: string | null;
  count: number;
  setCount: (newCount: number) => void;
  field: TextCollectionField;
  setSubmitCodeError?: (field_id: string, error: string) => void;
}

export const renderRunResponse = (runResponse: RunUnitTestsResponse) => {
  const { output, error } = runResponse;
  if (!output && error) {
    return (
      <Typography.Paragraph2 className="bg-warning-50 rounded-md px-4 py-2">
        <strong>Error:</strong> {error}
      </Typography.Paragraph2>
    );
  }

  return (
    <div className="space-y-2">
      {output && !error && (
        <>
          <div className="space-y-2">
            {/* Passes */}
            {(output.passes ?? -1) > 0 && (
              <Typography.Paragraph2 className="bg-success-50 rounded-md px-4 py-2">
                <strong>Passes:</strong> {output.passes}
              </Typography.Paragraph2>
            )}

            {/* Fails (expandable if with details) */}
            {(output.fails ?? -1) > 0 &&
              ((output.failed_suites?.length ?? -1) > 0 ? (
                <Accordion
                  className="bg-danger-50 rounded-md"
                  header={
                    <Typography.Paragraph2>
                      <strong>Fails:</strong> {output.fails}
                    </Typography.Paragraph2>
                  }
                >
                  <Typography.Paragraph2 className="space-y-2">
                    {output.failed_suites?.map((test, index) => (
                      <div key={index}>{test}</div>
                    ))}
                  </Typography.Paragraph2>
                </Accordion>
              ) : (
                <Typography.Paragraph2 className="bg-danger-50 rounded-md px-4 py-2">
                  <strong>Fails:</strong> {output.fails}
                </Typography.Paragraph2>
              ))}

            {/* Pass rate (expandable if with details) */}
            <Typography.Paragraph2 className="bg-neutral-50 rounded-md px-4 py-2">
              <strong>Pass Rate:</strong> {output.pass_rate?.toFixed(2)}%
            </Typography.Paragraph2>

            {/* Test coverage (expandable if with details) */}
            {output.coverage &&
              output.coverage >= 0 &&
              (output.jest_results ? (
                <Accordion
                  className="bg-neutral-50 rounded-md"
                  header={
                    <Typography.Paragraph2 className="flex items-center w-full">
                      <strong className="flex-shrink-0">Test Coverage:</strong>
                      <Progress
                        className="flex-grow mx-2"
                        percentComplete={output.coverage}
                        showPercent
                        size="md"
                      />
                    </Typography.Paragraph2>
                  }
                >
                  <Typography.Paragraph2 className="space-y-2">
                    {[
                      { label: 'Statement', value: output.coverage_statements },
                      { label: 'Function', value: output.coverage_functions },
                      { label: 'Branch', value: output.coverage_branches },
                    ].map(
                      (coverage, index) =>
                        coverage.value &&
                        coverage.value > 0 && (
                          <div key={index}>
                            <div>{coverage.label}:</div>
                            <Progress percentComplete={coverage.value} showPercent size="md" />
                          </div>
                        ),
                    )}
                  </Typography.Paragraph2>
                </Accordion>
              ) : (
                <Typography.Paragraph2 className="flex items-center w-full bg-neutral-50 rounded-md px-4 py-2">
                  <strong className="flex-shrink-0">Test Coverage:</strong>
                  <Progress
                    className="flex-grow mx-2"
                    percentComplete={output.coverage}
                    showPercent
                    size="md"
                  />
                </Typography.Paragraph2>
              ))}
          </div>

          {/* Results (JSON) */}
          {output.json_results && (
            <Typography.Paragraph2 className="bg-neutral-50 rounded-md px-4 py-2">
              <strong>Results:</strong>
              {output.json_results.map(result => (
                <div key={result.idx} className="my-2">
                  <strong>
                    {result.passed ? '✅' : '❌'} Test {result.idx + 1}
                  </strong>
                  <p>Input: {result.input.toString()}</p>
                  <p>Expected Output: {result.output}</p>
                </div>
              ))}
            </Typography.Paragraph2>
          )}

          {/* Results (PyTest) */}
          {output.pytest_results && (
            <Typography.Paragraph2 className="bg-neutral-50 rounded-md px-4 py-2 max-h-[400px] overflow-y-auto whitespace-pre-wrap">
              <strong>Results: </strong>
              {output.pytest_results}
            </Typography.Paragraph2>
          )}

          {/* Results (Jest) */}
          {output.jest_results && (
            <Typography.Paragraph2 className="bg-neutral-50 rounded-md px-4 py-2 max-h-[400px] overflow-y-auto whitespace-pre-wrap">
              <strong>Results: </strong>
              {output.jest_results}
            </Typography.Paragraph2>
          )}

          {/* Results (StdIn) */}
          {output.stdin_results && (
            <Typography.Paragraph2 className="bg-neutral-50 rounded-md px-4 py-2">
              <strong>Results:</strong>
              {output.stdin_results.map((result, index) => (
                <div key={index} className="my-2">
                  <strong>
                    {result ? '✅' : '❌'} Test {index + 1}:
                  </strong>
                  {result ? ' Passed' : ' Failed'}
                </div>
              ))}
            </Typography.Paragraph2>
          )}

          {/* Sphere Error */}
          {output.error && (
            <Typography.Paragraph2 className="bg-warning-50 rounded-md px-4 py-2">
              <strong>Error:</strong> {output.error}
            </Typography.Paragraph2>
          )}
        </>
      )}
    </div>
  );
};

// eslint-disable-next-line complexity
const UnitTestGenerationForm: React.FC<UnitTestGenerationFormProps> = ({
  codingUnitTestsGenerationConfig,
  annotations,
  setAnnotations,
  projectId,
  assignmentId = '',
  lastPrompt,
  lastModelResponseEditorContent,
  count,
  setCount,
  field,
  setSubmitCodeError,
}) => {
  const [language, setLanguage] = useState(Language.Python);
  const [format, setFormat] = useState(formatConfig[language]?.default || Format.PyTest);
  const [numTestCases, setNumTestCases] = useState<number>(1);
  const [methodName, setMethodName] = useState<string>(''); // for JSON format only
  const [runResponse, setRunResponse] = useState<RunUnitTestsResponse | undefined>(undefined);

  const codeFieldId = codingUnitTestsGenerationConfig?.codeFieldId;
  const maxTestCases = codingUnitTestsGenerationConfig?.numberOfTestCases ?? 10;
  const useCodeFromMostRecentResponseEditor =
    codingUnitTestsGenerationConfig?.useCodeFromMostRecentResponseEditor;
  const unitTestsFieldId = codingUnitTestsGenerationConfig?.unitTestsFieldId;
  const enableRunTests = codingUnitTestsGenerationConfig?.enableRunTests;

  const codeText = useCodeFromMostRecentResponseEditor
    ? lastModelResponseEditorContent
    : codeFieldId !== undefined && annotations[codeFieldId]?.response?.[0];
  const unitTestsText =
    unitTestsFieldId !== undefined && annotations[unitTestsFieldId]?.response?.[0];

  const missingCode = !codeText;
  const missingTests = !unitTestsText;

  const { mutate: runUnitTestsMutation, isLoading: isRunningUnitTests } = useMutation({
    mutationFn: async () => {
      const codeAnnotation = useCodeFromMostRecentResponseEditor
        ? lastModelResponseEditorContent
        : codeFieldId && annotations[codeFieldId]?.response?.[0];
      const unitTests = unitTestsFieldId && annotations[unitTestsFieldId]?.response?.[0];

      const requestParams: RunUnitTestsRequest = {
        language,
        format,
        code: codeAnnotation.toString(),
        assignmentId,
        unitTests: unitTests.toString(),
        methodName,
        hideOutput: false,
      };

      setRunResponse(undefined);
      const unitTestResults = await API.genai.runUnitTests(requestParams);
      return unitTestResults;
    },
    onSuccess: response => {
      setRunResponse(response);
    },
    onError: (error: any) => {
      setRunResponse({ error: `Error running unit tests: ${error.message}` });
    },
  });

  useEffect(() => {
    if (
      (field as UnitField).unit_test_pass_required ||
      (field as UnitField).unit_test_full_coverage_required
    ) {
      setSubmitCodeError?.(field.field_id, 'Please run visible unit tests.');
    }
  }, [codeText, unitTestsText, field, setSubmitCodeError]);

  useEffect(() => {
    let message = '';
    if ((field as UnitField).unit_test_pass_required) {
      if ((runResponse?.output?.passes ?? -1) <= 0 || (runResponse?.output?.fails ?? -1) !== 0) {
        message = 'Please pass all visible unit tests.';
      }
    }
    if ((field as UnitField).unit_test_full_coverage_required) {
      if ((runResponse?.output?.coverage ?? 100) !== 100) {
        message = 'Please ensure 100% test coverage.';
      }
    }
    setSubmitCodeError?.(field.field_id, message);
  }, [runResponse, field, setSubmitCodeError]);

  const { mutate: generateUnitTestsMutation, isLoading: isGeneratingUnitTests } = useMutation({
    mutationFn: async () => {
      const codeAnnotation = useCodeFromMostRecentResponseEditor
        ? lastModelResponseEditorContent
        : codeFieldId && annotations[codeFieldId]?.response?.[0];
      const requestParams: GenerateUnitTestsRequest = {
        projectId,
        prompt: lastPrompt ?? 'Generate unit tests for the given code.',
        code: codeAnnotation.toString(),
        language,
        numTestCases,
        format,
      };

      const unitTests = await API.genai.generateUnitTests(requestParams);

      if (unitTests.error) {
        return 'Failed to load unit tests. Error: ' + unitTests.error;
      } else {
        return parseUnitTests(unitTests.output);
      }
    },
    onSuccess: unitTestsString => {
      updateUnitTestsTextField(unitTestsString);
      setRunResponse(undefined);
    },
    onError: (error: any) => {
      updateUnitTestsTextField(`Error generating unit tests: ${error.message}`);
      setRunResponse(undefined);
    },
  });

  useEffect(() => {
    if (formatConfig[language]) {
      setFormat(formatConfig[language].default);
    }
  }, [language]);

  const updateUnitTestsTextField = (text: string) => {
    if (!unitTestsFieldId) {
      return;
    }
    setAnnotations({
      ...annotations,
      [unitTestsFieldId]: {
        ...annotations[unitTestsFieldId],
        response: [text],
      },
    });
    setCount(count + 1);
  };

  const parseUnitTests = (content: any): string => {
    try {
      if (content.pytest_file_content) {
        return content.pytest_file_content.replace(/\\n/g, '\n');
      } else if (content.jest_file_content) {
        return content.jest_file_content.replace(/\\n/g, '\n');
      } else {
        return JSON.stringify(content, null, 2);
      }
    } catch (error) {
      return `Error parsing unit tests content. Error: ${error}`;
    }
  };

  const numTestCasesOptions = Array.from({ length: maxTestCases }, (_, index) => ({
    id: (index + 1).toString(),
    name: (index + 1).toString(),
  }));

  return (
    <div className="space-y-2">
      <div className="flex items-center">
        <div className="space-y-2">
          <div className="flex space-x-2">
            <SelectMenu
              label="Language"
              options={Object.values(Language).map(lang => ({ id: lang, name: lang }))}
              selectedOptionId={language}
              setSelected={setLanguage}
            />
            <SelectMenu
              label="Format"
              options={
                formatConfig[language]?.allowed?.map(format => ({ id: format, name: format })) || []
              }
              selectedOptionId={format}
              setSelected={setFormat}
            />
            <SelectMenu
              label="Test Count"
              options={numTestCasesOptions}
              selectedOptionId={numTestCases.toString()}
              setSelected={id => setNumTestCases(Number(id))}
            />
            {format === Format.JSON && (
              <TextField
                label="Method Name"
                value={methodName}
                onChange={e => setMethodName(e.target.value)}
              />
            )}
          </div>
        </div>
        <div className="ml-auto flex flex-col space-y-2">
          <Button
            variant="secondary"
            className="flex justify-center"
            disabled={missingCode || !missingTests || isGeneratingUnitTests}
            onClick={() => generateUnitTestsMutation()}
          >
            {isGeneratingUnitTests ? 'Generating...' : 'Generate Tests'}
          </Button>
          {isRunningUnitTests && <Spinner size="xs" />}
          {enableRunTests && !isRunningUnitTests && (
            <Button
              variant="secondary"
              className="flex justify-center"
              disabled={missingCode || missingTests || !formatConfig[language]}
              onClick={() => runUnitTestsMutation()}
            >
              Run Visible Tests
            </Button>
          )}
        </div>
      </div>
      {runResponse && renderRunResponse(runResponse)}
    </div>
  );
};

export default UnitTestGenerationForm;
